import { useCallback, useMemo } from 'react';
import type {
  ColumnDef,
  CellContext,
  SortingState,
} from '@tanstack/react-table';
import { Button } from '@carbon/react';
import { Location } from '@carbon/react/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  DataTable,
  ExportButton,
  type ExportConfig,
  type TableState,
} from 'components/data-table';
import { LinkCell } from 'components/link-cell';
import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';
import {
  mapSearchRefAtom,
  SUPPRESS_BOUNDS_SEARCH,
  wellsResultsViewAtom,
} from '../atoms';
import type { DatasetColumnConfig } from '../types';
import type { MapQueryExport } from '../use-map-query-export';
import { CreateWorkspaceMenu } from '../create-workspace-menu';
import { ResultsViewDropdown } from './workspaces-results';
import { MapLocatorButton } from '../subsurface-maps/map-locator-button';
import { ShowOnMapToggle } from './show-on-map-toggle';
import { useTableAreaSelection } from '../create-area/hooks';
import {
  NO_SELECTED_SUBSURFACE_MAP,
  selectedSubsurfaceMapAtom,
} from '../subsurface-maps/atoms';
import {
  useDatasetExport,
  useDatasetSearchChange,
  useSelectHighlighted,
} from './hooks';
import type { Bounds, RowData } from './types';
import {
  WELLS_DATASET_ID,
  currentPaging,
  isSubsurfaceMapRow,
  rowExtent,
} from './utils';
import styles from './results-section.module.scss';

const NO_ROWS: RowData[] = [];

/**
 * A table row's id: its id, else (wells) its UWI.
 * @param {RowData} row The row.
 * @return {string} The id.
 */
const tableRowId = (row: RowData): string => {
  const id = row.id ?? row.uwi;
  return typeof id === 'number' || typeof id === 'string' ? String(id) : '';
};

// Hoisted so the toolbar receives the same array on every render.
const CREATE_WORKSPACE_ACTIONS = [
  <CreateWorkspaceMenu key="create-workspace" />,
];

// Without area selection, the table's options as they have always been.
const TABLE_CONFIG = { columnFilters: false };

// With it: multi-select checkboxes, which only own selection (no row clicks).
const SELECTABLE_TABLE_CONFIG = {
  columnFilters: false,
  selectionType: 'multi' as const,
};

/**
 * The table column for one configured dataset column.
 * @param {DatasetColumnConfig} col The configured column.
 * @return {ColumnDef} The table column.
 */
const buildColumnDef = (
  col: DatasetColumnConfig
): ColumnDef<RowData, unknown> => {
  const width = col.size ?? 150;
  const base = {
    accessorKey: col.accessorKey,
    id: col.id,
    header: col.header,
    size: width,
    minSize: width,
    enableSorting: col.sortable !== false,
  };

  // Column-level meta merged into every branch below: `wrap` lets long text
  // wrap instead of ellipsizing (entity names show in full), and
  // `align` overrides the auto alignment (e.g. left-align numeric columns so
  // their values sit beside the previous column instead of the far edge).
  const wrapMeta = col.wrap ? { wrap: true as const } : undefined;
  const alignMeta = col.align ? { align: col.align } : undefined;
  /**
   * A column's meta merged with its wrap and align settings.
   * @param {Record<string, unknown>} [meta] The branch's own meta.
   * @return {object} The `meta` prop, or nothing when empty.
   */
  const withWrap = (meta?: Record<string, unknown>) => {
    const merged = { ...meta, ...wrapMeta, ...alignMeta };
    return Object.keys(merged).length > 0 ? { meta: merged } : {};
  };

  if (col.formatValue) {
    type Info = CellContext<RowData, unknown>;
    return {
      ...base,
      ...withWrap(),
      cell: (info: Info) => col.formatValue!(info.getValue()) ?? '',
    };
  }

  switch (col.type) {
    case 'link':
      return {
        ...base,
        ...withWrap(),
        cell: (info: CellContext<RowData, unknown>) => (
          <LinkCell
            value={info.getValue() as string | null}
            href={
              col.rowHref
                ? col.rowHref(info.row.original)
                : (info.row.original[col.hrefKey ?? ''] as string | null)
            }
            showTitle={!col.wrap}
          />
        ),
      };
    case 'number':
      return {
        ...base,
        ...withWrap({
          type: col.decimals && col.decimals > 0 ? 'decimal' : 'integer',
          format: {
            minDecimalPlaces: col.decimals ?? 0,
            maxDecimalPlaces: col.decimals ?? 0,
          },
        }),
      };
    case 'date':
      return { ...base, ...withWrap({ type: 'date' as const }) };
    case 'datetime':
      return { ...base, ...withWrap({ type: 'datetime' as const }) };
    case 'text':
    case undefined:
      return { ...base, ...withWrap() };
  }
};

/**
 * Selects a row and moves the map to it: fits a subsurface map's footprint
 * extent, else flies to the row's point.
 * @return {Function} Shows a row on the map.
 */
const useShowOnMap = () => {
  const config = useDatasetConfig();
  const mapRef = useAtomValue(mapSearchRefAtom);
  const selectHighlighted = useSelectHighlighted();
  return useCallback(
    (row: RowData) => {
      if (!config.geoLocationKey && !config.extentKey) return;
      const idKey = config.highlightRowKey ?? config.columns[0].accessorKey;
      selectHighlighted(
        String(row[idKey]),
        (row.mapBounds as Bounds | undefined) ?? null
      );
      // An extent (a subsurface map's footprint bounds) fits the whole shape.
      const extent = rowExtent(row, config.extentKey);
      if (mapRef && extent) {
        const [west, south, east, north] = extent;
        mapRef.fitBounds(
          [
            [west, south],
            [east, north],
          ],
          { padding: 32, duration: 800 },
          SUPPRESS_BOUNDS_SEARCH
        );
        return;
      }
      if (!config.geoLocationKey) return;
      const geo = row[config.geoLocationKey] as
        { coordinates: [number, number] } | undefined;
      if (mapRef && geo) {
        const [lng, lat] = geo.coordinates;
        // Tag the move so visible-extent mode doesn't rebound the bounded
        // search and drop the selection we just made.
        mapRef.flyTo(
          { center: [lng, lat], zoom: 14, speed: 1.5 },
          SUPPRESS_BOUNDS_SEARCH
        );
      }
    },
    [mapRef, selectHighlighted, config]
  );
};

/**
 * The configured columns, led by the "show on map" action column when the
 * dataset is mappable.
 * @return {ColumnDef[]} The table columns.
 */
const useColumns = () => {
  const config = useDatasetConfig();
  const handleShowOnMap = useShowOnMap();
  const selectHighlighted = useSelectHighlighted();
  return useMemo(() => {
    const cols: ColumnDef<RowData, unknown>[] =
      config.columns.map(buildColumnDef);

    if (config.geoLocationKey || config.extentKey) {
      cols.unshift({
        id: 'actions',
        header: '',
        // Icon-only "show on map" button. Per-dataset width (defaults to 70) so
        // a dataset can tighten it to free room for data columns.
        size: config.actionColumnSize ?? 70,
        enableSorting: false,
        meta: { isExportable: false },
        cell: (info: CellContext<RowData, unknown>) => {
          const row = info.row.original;
          if (config.rasterLocator && isSubsurfaceMapRow(row)) {
            return (
              <MapLocatorButton
                row={row}
                onSelect={() => handleShowOnMap(info.row.original)}
                onClear={() => selectHighlighted(null, null)}
              />
            );
          }
          // An extent-only dataset row without an extent has nothing to fit to.
          const nothingToShow =
            !!config.extentKey &&
            !config.geoLocationKey &&
            !rowExtent(info.row.original, config.extentKey);
          if (config.showOnMapToggles) {
            const idKey =
              config.highlightRowKey ?? config.columns[0].accessorKey;
            return (
              <ShowOnMapToggle
                rowId={String(info.row.original[idKey])}
                disabled={nothingToShow}
                onShow={() => handleShowOnMap(info.row.original)}
                onClear={() => selectHighlighted(null, null)}
              />
            );
          }
          return (
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Location}
              iconDescription={
                nothingToShow ? 'No map footprint to show' : 'Show on map'
              }
              hasIconOnly
              disabled={nothingToShow}
              onClick={() => handleShowOnMap(info.row.original)}
            />
          );
        },
      });
    }

    return cols;
  }, [config, handleShowOnMap, selectHighlighted]);
};

/**
 * Subsurface Maps: the rows that read as selected (v3) — the one whose raster is
 * on the map, and the footprint selected (clicked) on the map.
 * @return {Set<string> | undefined} The selected row ids.
 */
const useRasterRowIds = () => {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const highlightId = useAtomValue(atoms.highlightedItemIdAtom);
  // Other datasets read a constant atom, so showing a raster never re-renders them.
  const shownRaster = useAtomValue(
    config.rasterLocator
      ? selectedSubsurfaceMapAtom
      : NO_SELECTED_SUBSURFACE_MAP
  );
  return useMemo(() => {
    if (!config.rasterLocator || (!shownRaster && !highlightId))
      return undefined;
    const ids = new Set<string>();
    if (shownRaster) ids.add(String(shownRaster.id));
    if (highlightId) ids.add(String(Number(highlightId)));
    return ids;
  }, [config.rasterLocator, shownRaster, highlightId]);
};

/**
 * The flat table layout: server-driven paging, sorting and search, with the
 * show-on-map column and CSV export.
 * @param {object} props The table.
 * @param {MapQueryExport} props.mapQueryExport The page's CSV export.
 * @return {JSX.Element} The table.
 */
export function ResultsTable({
  mapQueryExport,
}: {
  mapQueryExport: MapQueryExport;
}) {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const { data, loading, currentPayload } = useAtomValue(atoms.searchAtom);
  const setSearch = useSetAtom(atoms.searchAtom);
  const datasetText = useAtomValue(atoms.datasetTextAtom);
  const handleSearchChange = useDatasetSearchChange();
  const columns = useColumns();
  const rasterRowIds = useRasterRowIds();
  // Checkboxes whose rows seed Create New Area; null for a dataset that can't
  // seed one, leaving the table unchanged.
  const areaSelection = useTableAreaSelection(
    config,
    data?.results ?? NO_ROWS,
    tableRowId
  );
  const { exportDataset, disabledReason } = useDatasetExport(mapQueryExport);
  const exportConfig: ExportConfig = {
    exportPresets: [
      {
        name: 'Download',
        format: 'csv',
        // The button sends the visible, exportable columns in on-screen order,
        // and stays disabled (showing the reason) while an export can't start.
        columns: 'visible',
        disabledReason,
        onExport: (_format, _filters, _name, cols) => exportDataset(cols),
      },
    ],
  };

  const handleSortChange = useCallback(
    (newSorting: SortingState) => {
      const sort = newSorting.at(0);
      setSearch((current) => ({
        ...(current ?? config.defaultPayload),
        sortAttribute: sort?.id,
        sortDirection: sort ? (sort.desc ? 'DESC' : 'ASC') : undefined,
        page: 1,
      }));
    },
    [setSearch, config.defaultPayload]
  );

  // Server-driven pagination.
  const handlePaginationChange = useCallback(
    (state: TableState) => {
      setSearch((current) => ({
        ...(current ?? config.defaultPayload),
        page: state.page,
        pageSize: state.pageSize,
      }));
    },
    [setSearch, config.defaultPayload]
  );

  const [resultsView, setResultsView] = useAtom(wellsResultsViewAtom);
  const isWellsDataset = config.id === WELLS_DATASET_ID;

  return (
    <div className={styles.container}>
      <DataTable
        data={data?.results ?? NO_ROWS}
        columns={columns}
        getRowId={tableRowId}
        highlightedRowIds={rasterRowIds}
        isLoading={!!loading}
        persistenceKey={`${config.id}-search-results`}
        leftToolbarActions={[
          ...(isWellsDataset
            ? [
                <ResultsViewDropdown
                  key="wells-results-view"
                  value={resultsView}
                  onChange={setResultsView}
                />,
              ]
            : []),
          ...(config.canCreateDcaWorkspace ? CREATE_WORKSPACE_ACTIONS : []),
        ]}
        // Pagination is uncontrolled — the table seeds its page index once from
        // initialTableState on mount. This section unmounts when its tab is
        // inactive (see page.tsx), so on remount we seed from the atom's
        // currentPayload (the last-searched page), not defaultPayload's page 1.
        // Otherwise the paginator would read "1" while the cached rows are still
        // page N.
        initialTableState={currentPaging(currentPayload, config.defaultPayload)}
        // Server-driven: pagination, sorting, and text search are all handled
        // by /queries/wells. This disables the client-side global filter, which
        // otherwise re-filters the current page literally -- dropping every row
        // when the search term contains quotes.
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount: data?.totalCount ?? 0,
        }}
        config={areaSelection ? SELECTABLE_TABLE_CONFIG : TABLE_CONFIG}
        // Search is controlled by the per-dataset applied text (same atom the
        // cards-layout Search box reads) so it survives this section's remount —
        // a dataset-tab switch, or (for Wells) toggling the View dropdown to
        // Workspaces and back — instead of resetting blank every time.
        controlledState={
          areaSelection
            ? { globalFilter: datasetText, selection: areaSelection.selection }
            : { globalFilter: datasetText }
        }
        onSelectionChange={areaSelection?.onSelectionChange}
        onPaginationChange={handlePaginationChange}
        onSortChange={(s) => handleSortChange(s.sorting)}
        onGlobalSearchChange={(s) => handleSearchChange(s.globalFilter)}
        searchPlaceholder={`Search ${config.label}`}
        rightToolbarActions={[
          <ExportButton key="export" config={exportConfig} />,
        ]}
      />
    </div>
  );
}
